import { createPriceFormatter } from '@/utils/price-formatter'

export class LyVariantChangedEventListener extends HTMLElement {

    _variantConfigs = null
    _readyPromise = Promise.resolve()
    _latestVariantPayload = null

    _retrying = false

    connectedCallback() {
        this._readyPromise = this._initializeVariantConfigs()

        this.variantPickerChangeUnsubscriber = window.subscribe('bus:variant_picker_change', async (payload) => {
            this._latestVariantPayload = payload
            this._flushVariantChange()
        })
    }

    disconnectedCallback() {
        this.variantPickerChangeUnsubscriber?.()
    }

    _initializeVariantConfigs = async () => {

        const viewName = this.dataset.viewName

        // 优先使用 fetch
        if (!viewName) return

        try {
            const data = await this._fetchVariantConfigsWithRetry(viewName)

            // 👇 新增 hook
            this._variantConfigs = this._transformVariantConfigs(data)

            this._afterInitializedVariantConfigs()

            return
        } catch (error) {
            console.error(`[LyVariantChangedEventListener] 加载变体配置失败，2s后会刷新页面`, { viewName, error })
            setTimeout(() => location.reload(), 2000)
        }
    }

    async _fetchVariantConfigsWithRetry(viewName, maxRetries = 3) {
        let lastError = null

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {

                // 防止 shopify cli dev 的 304 bug
                const url = `${window.location.pathname}` + `?view=${viewName}` + `&_=${Date.now()}`

                const res = await fetch(url, { cache: 'no-store' })

                if (!res.ok) {
                    throw new Error(`请求失败 status=${res.status}`)
                }

                return await res.json()

            } catch (error) {
                lastError = error

                console.warn(`[LyVariantChangedEventListener] 第 ${attempt + 1} 次请求失败`, error)

                // 最后一次不等待
                if (attempt >= maxRetries) {
                    break
                }

                // 指数退避
                const delay = 300 * Math.pow(2, attempt)

                await new Promise(resolve => setTimeout(resolve, delay))
            }
        }

        throw lastError
    }

    _transformVariantConfigs = data => data

    _afterInitializedVariantConfigs = () => { }

    _flushVariantChange = async () => {

        await this._readyPromise

        const payload = this._latestVariantPayload

        if (!payload) return

        this._latestVariantPayload = null

        this._beforeVariantChange(payload)
        this._onVariantChange(payload)
    }

    _beforeVariantChange = ({ variantId, activedKey, activedOptions }) => { }

    _onVariantChange = ({ variantId, activedKey, activedOptions }) => { }
}

export class LyMainProductFormBase extends LyVariantChangedEventListener {

    formatter = createPriceFormatter()

    connectedCallback() {
        super.connectedCallback()
        this.queryWrapper = document.querySelector('ly-main-product .ly-main-product-form-wrapper')
    }

    disconnectedCallback() {
        super.disconnectedCallback()
    }

    get sidewallCheckedInputs() {
        const allInputs = [...this.queryWrapper.querySelectorAll('.ly-main-product-form-sidewall-picker-side-popover-sidewall-picker-checkbox')]
        return allInputs.filter(input => input.checked)
    }

    get sidewallAccessoryCheckedInputs() {
        return this.sidewallCheckedInputs.map(sidewallCheckedInput => {
            const sidewall = sidewallCheckedInput.closest('ly-main-product-form-sidewall-picker-side-popover-sidewall')
            return sidewall.querySelector('.ly-main-product-form-sidewall-picker-side-popover-sidewall-accessory-item-picker-radio:checked')
        }).filter(Boolean)
    }

    get accessoryCheckedResult() {
        const allInputs = [...this.queryWrapper.querySelectorAll('.ly-main-product-form-accessory-picker-item-picker-checkbox')]
        const checkedInputs = allInputs.filter(input => input.checked)
        return checkedInputs.map(checkedInput => {
            const quantityInput = checkedInput.closest('.ly-main-product-form-accessory-picker-item')
                .querySelector('.ly-main-product-form-accessory-picker-item-quantity-input')

            return { checkedInput, quantityInput }
        }).filter(item => !item.checkedInput.hasAttribute('data-disabled-for-cart'))
    }

    /** 获取现价 */
    getCurrentPrice = (dom, checkFlag = true) => {
        if (!dom) return 0
        if (checkFlag && dom.hasAttribute('data-in-sidewall-free-upgrade-activity')) return 0
        return +(dom.dataset.currentPrice || 0)
    }

    /** 获取原价 */
    getOriginalPrice = (dom, checkFlag = true) => {
        if (!dom) return 0
        return +(dom.dataset.originalPrice || this.getCurrentPrice(dom, checkFlag))
    }

    /** 获取安装服务现价 */
    getInstallationCurrentPrice = (dom) => {
        if (!dom) return 0
        return +(dom.dataset.installationServicePrice || 0)
    }

    /** 获取安装服务原价 */
    getInstallationOriginalPrice = (dom) => {
        if (!dom) return 0
        const p = +(dom.dataset.installationServiceOriginalPrice || 0)
        if (p > 0) return p
        return this.getInstallationCurrentPrice(dom)
    }

    createOrderSummarySkuConfigHtml = (variant, image) => {
        let pre_order_time = ''
        let stock_class = ''

        const { stockConfig } = document.querySelector('ly-main-product-form-stock-overview')

        if (variant.preOrderTime && variant.inventory <= 0) {
            stock_class = 'pre-order'
            pre_order_time = variant.preOrderTime.time
        } else if (variant.inventory <= 0) {
            stock_class = 'no-stock'
        } else if (variant.inventory <= stockConfig.lowStockNum) {
            stock_class = 'low-stock'
        } else {
            stock_class = 'in-stock'
        }

        const stock_title = stockConfig[stock_class].title

        const config = {
            sku_id: variant.id,
            sku_image: variant.productImage || image,
            sku_title: variant.productTitle,
            sku_options: variant.options,
            pre_order_time,
            stock_class,
            stock_title
        }

        return `
        <script type="application/json" data-order-summary-sku-config>
          ${JSON.stringify(config)}
        </script>
        `
    }
}
